import React, { useState, useContext, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import loadable from '@loadable/component';
import 'isomorphic-unfetch';
import mergeWith from 'lodash/mergeWith';
import Panel from '@prisjakt/ui-panel';
import { ScrollElement } from '@prisjakt/ui-scroll';
import { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import {
  getCurrentCampaign,
  getHeaderHeight,
  getSimilarProductsCompareLink,
} from './utils';
import { AppState } from '../../reducers';
import modalActions from '../../components/modal/actions';
import AdContainer from '../../components/ads';
import FeedbackButton from '../feedback/feedback-button';
import Lazy from '../../components/lazy';
import Modal from '../../components/modal';
import PageContent from '../../components/page-content';
import RecentlyVisitedProducts from '../../components/recently-visited';
import Recommendations from '../../components/recommendations';
import StickyTrack from '../../components/sticky-track';
import TabList from './components/tab-list';
import { ViewportContext } from '../../components/viewport';
import { readPersonalizationSettings } from '../user/storage/personalization';
import productPageQuery from './query.graphql';
import {
  PRODUCT_ALERT_MODAL_ID,
  PRODUCT_CONTEXT_ID,
  mapOptionsToFilters,
  initialFilterState,
} from './constants';

import {
  Campaign,
  CampaignBanner,
  ProductPageQuery,
  User,
  Product,
} from '../../types';
import { Review, RouteSection, CurrentModal } from './types';
import {
  ModelType,
  ModelTypeEnum,
} from '../../components/recommendations/types';
import { setSelectedVariant, setSelectedCondition } from './actions/product';
import { trackComparisonLinkClick } from '../product-compare/tracking';
import * as S from './styles';
import {
  Variables,
  RouteReducerState,
  RouteReducerActions,
  Loaded,
  Group,
} from './product-page-types';
import ProductPageHeader from './components/product-header';
import ProductTabs from './components/product-tabs';
import ProductDuringPromoBanner from './components/product-promo-banner/product-during-promo-banner';
import ProductBreadCrumb from './components/product-breadcrumb';
import ProductBeforePromoBanner from './components/product-promo-banner/product-before-promo-banner';
import ProductScrollTopButton from './components/product-scroll-top-button';
import ProductCampaignDeals from './components/product-campaign-deals';
import { PriceListFilterContextContainer } from './price-list-filter-context';
import { useEffectOnMount } from '../../hooks/useEffectOnMount';
import ProductConditionSplitExperiment from './components/product-conditions/experiment';

const ProductAlertModalContent = loadable(() =>
  import(
    /* webpackChunkName: "extra" */ '../product-alert/components/product-alert-modal-content'
  )
);

const ProductPage = ({
  activeCampaign,
  data,
  fetchMoreData,
  routeSection = 'main',
  campaignBanners,
  currentModal,
  me,
  review,
  hideModal,
}: Props) => {
  const { t, linkTo, market } = useContext(AppContext);
  const [isHeaderVisible, setHeaderVisible] = useState<boolean>(true);
  const [campaigns, setCampaignsState] = useState([]);
  const dispatch = useDispatch();
  const { product, importantDates, recommendations } = data;
  const [productConditionVisible, setProductConditionVisible] = useState<
    boolean
  >(false);
  const selectedCondition = useSelector(
    (state: AppState) => state.areas.product.condition
  );
  useEffectOnMount(() => {
    dispatch(setSelectedVariant({ id: 0 }));
    dispatch(setSelectedCondition(productConditionVisible ? 'new' : null));
  });

  useEffect(() => {
    if (selectedCondition === null) {
      dispatch(setSelectedCondition(productConditionVisible ? 'new' : null));
    }
  }, [productConditionVisible, selectedCondition]);

  useEffect(() => {
    if (!IS_BROWSER || !product || !product.id) {
      return;
    }

    const dealsEndpoint = getDealsEndpoint(market);

    fetchDeals(dealsEndpoint, product.id).then(
      res =>
        res.hits &&
        res.hits.hits &&
        setCampaignsState(
          res.hits.hits.map((hit: any) => ({
            id: hit._source.campaign.id,
            price: { id: hit._source.priceInfo.priceId.toString() },
          }))
        )
    );
  }, [product && product.id]); // eslint-disable-line

  if (!product) return null;

  product.campaigns = campaigns;

  const privacyPolicyUri = market?.privacySettings?.responsive?.privacyUrl;

  const currentCampaign = getCurrentCampaign(activeCampaign, product);

  const userReviews = product.userReviews;

  const userReviewsSummary =
    review.userReviews && review.userReviews.summary
      ? review.userReviews.summary
      : product.userReviewSummary;

  const isPreRelease =
    product.releaseDate &&
    Date.parse(product.releaseDate) > Date.parse(new Date().toString());

  const similarProducts = recommendations?.find(
    item => item?.model === ModelTypeEnum.SIMILAR_PRODUCTS
  );

  const handleCompareLinkClick = () => {
    if (!similarProducts?.recommendations?.length) return null;

    return trackComparisonLinkClick(
      similarProducts.recommendations.length + (product ? 1 : 0),
      'similarProducts'
    );
  };

  const similarProductsCompareLink = getSimilarProductsCompareLink(
    similarProducts,
    product,
    linkTo
  );

  const getRecommendationsLink = (modelType: ModelType) => {
    if (
      modelType === ModelTypeEnum.SIMILAR_PRODUCTS &&
      similarProductsCompareLink
    )
      return {
        title: t('product', 'product-compare.compare'),
        url: similarProductsCompareLink,
        onClick: handleCompareLinkClick,
      };

    if (modelType === ModelTypeEnum.PERSONALIZED)
      return {
        title: t('account', 'menu.settings'),
        url: '/personalization',
      };

    return null;
  };

  // We want to test rendering all tabbed sections on load in DK
  // by measuring the SEO vs performance impact.
  // All tabs and rendered in the DOM but non active are hidden with CSS.
  const shouldRenderAllTabs = market.code === 'dk';

  return (
    <>
      {me && me.analyticsAccess && (
        <FeedbackButton position="right">
          {t('feedback', 'button.text')}
        </FeedbackButton>
      )}

      <PageContent withAds>
        <ProductConditionSplitExperiment
          setUseProductCondition={setProductConditionVisible}
          category={product.category}
        />
        <S.ProductPage>
          {currentModal === PRODUCT_ALERT_MODAL_ID && (
            <Modal
              id={PRODUCT_ALERT_MODAL_ID}
              onRequestClose={hideModal}
              width={500}
            >
              <ProductAlertModalContent
                productId={product.id}
                hideModal={hideModal}
                privacyPolicyUri={privacyPolicyUri || undefined}
              />
            </Modal>
          )}
          <ProductDuringPromoBanner
            product={product}
            campaignBanners={campaignBanners}
          />

          <ProductBreadCrumb product={product} />

          <ScrollElement name="Top">
            <PriceListFilterContextContainer>
              <ViewportContext.Consumer>
                {({ isMobile }) => (
                  <>
                    <ProductPageHeader
                      product={product}
                      isMobile={isMobile}
                      isHeaderVisible={isHeaderVisible}
                      setHeaderVisible={setHeaderVisible}
                      currentCampaign={currentCampaign}
                    />

                    <ProductScrollTopButton />

                    <ScrollElement name="Content">
                      <StickyTrack
                        topMargin={0}
                        headerHeight={getHeaderHeight(isMobile)}
                        renderElement={
                          <TabList
                            routeSection={routeSection}
                            product={product}
                            isSticky={!isHeaderVisible}
                            productConditionVisible={productConditionVisible}
                            selectedCondition={selectedCondition}
                          />
                        }
                      >
                        <S.TabContentWrapper
                          spacingTop={routeSection !== 'main'}
                        >
                          <ProductTabs
                            shouldRenderAllTabs={shouldRenderAllTabs}
                            routeSection={routeSection}
                            isPreRelease={isPreRelease}
                            product={product}
                            similarProducts={similarProducts}
                            importantDates={importantDates}
                            fetchMoreData={fetchMoreData}
                            getRecommendationsLink={getRecommendationsLink}
                            userReviews={userReviews}
                            currentCampaign={currentCampaign}
                            userReviewsSummary={userReviewsSummary}
                            productConditionVisible={productConditionVisible}
                          />

                          <Panel>
                            <AdContainer
                              placement="panorama_target"
                              format="panorama_3_wde"
                              min={861}
                              max={10000}
                              scalable
                              scaleBase=".pj-ui-panel--row"
                              triggerPoint={100}
                            />
                          </Panel>

                          <ProductCampaignDeals product={product} />

                          <section>
                            <Lazy offset={100}>
                              {recommendations?.map(item => {
                                if (!item.recommendations?.length) return null;
                                return (
                                  <Recommendations
                                    key={`recommendations-model-${item.model}`}
                                    link={getRecommendationsLink(
                                      item.model as ModelType
                                    )}
                                    recommendations={item.recommendations}
                                    type={item.model as ModelType}
                                    contextId={PRODUCT_CONTEXT_ID}
                                  />
                                );
                              })}
                            </Lazy>

                            <Lazy offset={100}>
                              <RecentlyVisitedProducts offset={1} />
                            </Lazy>
                          </section>
                          <ProductBeforePromoBanner
                            campaignBanners={campaignBanners}
                          />
                        </S.TabContentWrapper>
                      </StickyTrack>
                    </ScrollElement>
                  </>
                )}
              </ViewportContext.Consumer>
            </PriceListFilterContextContainer>
          </ScrollElement>
        </S.ProductPage>
      </PageContent>
    </>
  );
};

type OwnProps = {
  activeCampaign: Campaign;
  data: ProductPageQuery & { product: Product };
  fetchMoreData: () => void;
  routeSection: RouteSection;
};

type StateProps = {
  campaignBanners: {
    active: (CampaignBanner & {
      campaign: Pick<Campaign, 'media'>;
    })[];
  };
  currentModal: CurrentModal;
  me: User;
  review: Review;
};

type DispatchProps = {
  hideModal: () => void;
};

type Props = OwnProps & StateProps & DispatchProps;

ProductPage.graphQL = productPageQuery;

// Group on product ID, ignore section param
ProductPage.group = ({ name, match }: Group) =>
  `${name}_${match && match.params ? match.params.id : 'unknown'}`;

ProductPage.variables = ({
  id,
  offset,
  section,
  marketCode,
  experiments,
}: Variables) => {
  const excludeTypes = Object.keys(initialFilterState)
    .filter(option => !initialFilterState[option])
    .map(filter => mapOptionsToFilters[filter])
    .filter(x => x);

  const expertsEnabled = ['se'].includes(marketCode);
  const conditionEnabled = !(
    experiments['product-condition']?.selectedVariant === 'baseline'
  );

  return {
    id: parseInt(id, 10),
    offset: parseInt(offset, 10) || 0,
    section,
    statisticsTime: section === 'statistics' ? '1970-01-02' : undefined,
    marketCode,
    personalizationExcludeCategories: readPersonalizationSettings().notInterestedIn.categories.map(
      (category: { id: string }) => +category.id
    ),
    recommendationsContextId: PRODUCT_CONTEXT_ID,
    includeSecondary: false,
    excludeTypes,
    variants: null,
    advized: expertsEnabled,
    condition: conditionEnabled ? 'new' : null,
  };
};

ProductPage.reducer = (
  state: RouteReducerState,
  action: RouteReducerActions
) => {
  switch (action.type) {
    case 'PRICE_FILTER':
      return {
        ...state,
        options: action.options,
        variants: action.variants,
        condition: action.condition,
      };

    case 'PRODUCT_VARIANTS':
      return {
        ...state,
        variants: action.variants,
        options: action.options,
        condition: action.condition,
      };

    case 'PRODUCT_CONDITION':
      return {
        ...state,
        condition: action.condition,
        options: action.options,
      };

    default:
      return state;
  }
};

ProductPage.stateToVariables = (state: RouteReducerState) => {
  if (!state || !state.options) return {};

  const excludeTypes = Object.keys(state.options)
    .filter(option => !state.options[option])
    .map(filter => mapOptionsToFilters[filter])
    .filter(x => x);

  return {
    ...state,
    excludeTypes,
    includeSecondary: state.options?.showInternational,
    priceList: true,
    variants: state.variants,
    condition: state.condition,
  };
};

ProductPage.loadOrder = (
  { section = 'main', marketCode = undefined } = {},
  loaded = {} as Loaded,
  options = { initialRoute: false }
) => {
  const sectionContent = {
    main: {
      priceList: true,
    },
    properties: {
      productProperties: true,
    },
    reviews: {
      userReviews: true,
    },
    statistics: {
      priceHistory: true,
      importantDates: true,
      priceHistoryTable: true,
      priceTrend: true,
      medianPrice: true,
    },
  };

  const firstLoad =
    // We need to fetch all sections data for the experiment being run in DK
    marketCode === 'dk'
      ? {
          ...sectionContent.main,
          ...sectionContent.properties,
          ...sectionContent.reviews,
          ...sectionContent.statistics,
        }
      : {
          ...sectionContent[section],
        };

  return [
    {
      main: !loaded.main,
      ...firstLoad,
    },
    {
      // Refresh price list for cached pages only
      priceList: options.initialRoute && section === 'main',
      userActions: !loaded.userActions,
      badges: !loaded.badges,
      media: !loaded.media,
      campaign: !loaded.campaign,
      relatedProducts: !loaded.relatedProducts,
      campaignDeals: !loaded.campaignDeals,
      priceHistory: !loaded.priceHistory,
    },
  ];
};

ProductPage.mergeData = (
  oldData: ProductPageQuery,
  newData: ProductPageQuery
) => {
  const data = mergeWith({}, oldData, newData);

  // If price data was refetched always use data from latest fetch
  if (newData?.product?.prices) {
    return {
      ...data,
      product: {
        ...data.product,
        prices: newData.product.prices,
        variantGroups: newData.product.variantGroups,
      },
    };
  }

  return data;
};

const mapStateToProps = (state: AppState) => ({
  campaignBanners: state.globals.campaignBanners,
  review: state.areas.review,
  currentModal: state.modal.current,
  me: state.areas.account.me,
});

const dispatchProps = {
  hideModal: modalActions.hideModal,
};

const getDealsEndpoint = (market: ClientMarket) => {
  return `${market.campaignsBaseUrl}/campaign-${market.code}-*-deals/_search`;
};

const fetchDeals = (dealsEndpoint: string, productId: number) => {
  return fetch(dealsEndpoint, {
    method: 'POST',
    body: JSON.stringify({
      _source: ['priceInfo.priceId', 'campaign.id'],
      query: {
        bool: {
          filter: {
            terms: { 'product.id': [productId] },
          },
        },
      },
    }),
  })
    .catch(err => {
      const fetchError = new Error(
        `FetchError: ${err.message} ${dealsEndpoint}`
      );
      throw fetchError;
    })
    .then(res => res.json());
};

export default connect(mapStateToProps, dispatchProps)(ProductPage);
